import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Components } from 'src/Libs/shop'
import { SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM } from 'src/Libs/shop/consts'
import { ProgressBarSteps } from 'src/Libs/shop/types'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import useModals from 'src/Hooks/useModals'
import {
  addProduct,
  addWarrantyExtension,
  decreaseQuantity,
  increaseQuantity,
  removeProduct,
  removeWarrantyExtension,
} from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationAction'
import useShopQntmSystemConfiguration from 'src/Libs/shop/hooks/use-qntm-system-configuration'
import { Product } from 'src/Libs/shop/models/system-configuration-components.model'

interface SelectedConfiguredProduct {
  id: string
  qty: number
}

interface UseShopQntmAdditionalComponentReturn {
  title: string
  categories: Components[]
  warrantyExtension: Product[] | undefined
  isWarrantyExtensionSelected: boolean
  isLoading: boolean
  onBack: () => void
  onMoreInfo: (productId: string) => void
  isProductSelected: (productId: string) => boolean
  getSelectedProduct: (productId: string) => SelectedConfiguredProduct | undefined
  onDecreaseProductQuantity: (categoryId: string, selectedProductId: string, selectedProductQty: number) => void
  onIncreaseProductQuantity: (categoryId: string, selectedProductId: string) => void
  onAddProductToCart: (categoryId: string, productId: string, minPurchase: number, productHint: string | false) => void
  onRemoveProductFromCart: (categoryId: string, productId: string) => void
  onAddWarrantyExtensionToCart: (productId: string) => void
  onRemoveWarrantyExtensionFromCart: () => void
}

/*
 * Handles data and product selection logic for the Quantum Additional Component step
 */
export const useShopQntmAdditionalComponent = (): UseShopQntmAdditionalComponentReturn => {
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { getQntmSystemConfigurationComponents } = useShopQntmSystemConfiguration()
  const { openShopMoreInfo, openShopHintModal } = useModals()

  const configuratorItems = useTypedSelector(state => state.qntmStorage.items)
  const selectedElement = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const components = configuratorItems.length > 0 ? configuratorItems[0].configuration.components : null
  const selectedWarrantyExtensionId = configuratorItems.length > 0 ? configuratorItems[0].configuration.warrantyExtension?.id : null
  const isWarrantyExtensionSelected = Boolean(selectedWarrantyExtensionId)
  const [categories, setCategories] = useState<Components[]>([])
  const [warrantyExtension, setWarrantyExtension] = useState<Product[]>()
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!selectedElement) {
      return navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.BasicSystem}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffectShopAccess(() => {
    setIsLoading(true)
    if (selectedElement) {
      getQntmSystemConfigurationComponents(selectedElement)
        .then(result => {
          const filteredCategories =
            result?.components
              .filter(category => category.products.length > 0 && category.mandatory === false)
              .sort((a, b) => a.position - b.position) ?? []

          setCategories(filteredCategories)
          setWarrantyExtension(result?.warrantyExtensions)
        })
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onBack = (): void => {
    navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.MainComponent}`)
  }

  const onMoreInfo = (productId: string): void => {
    openShopMoreInfo({ productId })
  }

  const getSelectedProduct = (productId: string): SelectedConfiguredProduct | undefined => {
    return components?.flatMap(component => component.products).find(selectedProduct => selectedProduct.id === productId)
  }

  const isProductSelected = (productId: string): boolean => {
    return Boolean(getSelectedProduct(productId))
  }

  const onDecreaseProductQuantity = (categoryId: string, selectedProductId: string, selectedProductQty: number): void => {
    if (selectedProductQty > 1) {
      selectedProductId && dispatch(decreaseQuantity(categoryId, selectedProductId))
    } else {
      selectedProductId && dispatch(removeProduct(categoryId, selectedProductId))
    }
  }

  const onIncreaseProductQuantity = (categoryId: string, selectedProductId: string): void => {
    selectedProductId && dispatch(increaseQuantity(categoryId, selectedProductId))
  }

  const onAddProductToCart = (categoryId: string, productId: string, minPurchase: number, productHint: string | false): void => {
    dispatch(addProduct(categoryId, productId, minPurchase))

    if (productHint) {
      openShopHintModal({ text: productHint })
    }
  }

  const onRemoveProductFromCart = (categoryId: string, productId: string): void => {
    dispatch(removeProduct(categoryId, productId))
  }

  const onAddWarrantyExtensionToCart = (productId: string): void => {
    dispatch(addWarrantyExtension(productId))
  }

  const onRemoveWarrantyExtensionFromCart = (): void => {
    dispatch(removeWarrantyExtension())
  }

  return {
    title: t('shop.additional-component.title'),
    categories,
    warrantyExtension,
    isWarrantyExtensionSelected,
    isLoading,
    onBack,
    onMoreInfo,
    isProductSelected,
    getSelectedProduct,
    onDecreaseProductQuantity,
    onIncreaseProductQuantity,
    onAddProductToCart,
    onRemoveProductFromCart,
    onAddWarrantyExtensionToCart,
    onRemoveWarrantyExtensionFromCart,
  }
}
